import React, { useMemo } from 'react';
import { AnimatePresence, AnimateSharedLayout } from 'framer-motion';
import {
  IMerchantThemeColorsVariants,
  IMerchantThemeColors,
} from '@olo-web/types/merchantTheme.interface';
import {
  useDisclosure,
  Stack,
  Text,
  Flex,
  ButtonGroup,
  Button,
  Box,
  HStack,
  Tooltip,
  Icon,
  useTheme,
  chakra,
  Link,
} from '@chakra-ui/react';
import { AnimatedColorBox } from '../AnimatedColorBox';
import { Banner } from '@olo-web/components/atoms/Banner';
import { MotionBox } from '@olo-web/components/atoms/motion/MotionBox.ui';
import { Info } from 'react-feather';
import { ColorPicker } from '../ColorPicker';
import { useCustomThemeDraftDispatch, useCustomThemeDraftState } from '@olo-web/client-state';
import { useMerchantTheme } from '@domain/merchants/queries/useMerchantTheme';
import { useUpdateMerchantTheme } from '@domain/merchants/mutations/useUpdateMerchantTheme';
import { useError } from '@olo-web/utils/common/hooks';
import isObject from 'lodash/isObject';
import { useGenerateShades } from '../hooks';
import { hex } from 'wcag-contrast';
import round from 'lodash/round';
import { useCombinedTheme } from '@olo-web/theme/hooks';
import { useToast } from '@olo-web/utils/common/hooks';
const tips = {
  primary: 'Your most prominent color. Often seen on important buttons.',
  base: 'Your secondary color. Adds some accent to your custom branding.',
};

export const ColorPaletteForm = ({ item }) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const themeDispatch = useCustomThemeDraftDispatch();
  const themeState = useCustomThemeDraftState();
  const { generateShades } = useGenerateShades();
  const { colors: combinedThemeColors } = useCombinedTheme();
  const { notify } = useToast();
  const callError = useError();
  const [name, colors]: ['primary' | 'base', IMerchantThemeColorsVariants] = useMemo(() => {
    const i = { ...item };
    const n = i.variant;
    delete i.variant;
    return [n, i];
  }, [item]);

  const { mutate, isLoading } = useUpdateMerchantTheme({
    onSuccess: () => {
      themeDispatch({
        type: 'CLEAR_COLOR',
        payload: name,
      });
      notify({
        status: 'success',
        title: `Your color has been updated`,
        description: 'Your changes are now live',
        variant: 'left-accent',
        position: 'bottom-right',
      });
      onClose();
    },
    onError: (error) => callError({ error, toastError: true, title: 'Theme not updated' }),
  });
  const { data: merchantTheme } = useMerchantTheme();
  const theme = useTheme();
  const handleChange = (key: number | string, value: string): void => {
    generateShades({ variant: name, key, value });
  };

  const handleSave = (): void => {
    if (merchantTheme?.id) {
      mutate({
        theme: {
          colors: {
            [name]: theme.colors[name] as IMerchantThemeColors,
          },
        },
      });
    }
  };

  const handleRestore = (): void => {
    themeDispatch({
      type: 'CLEAR_COLOR',
      payload: name,
    });
  };

  const localStateHasData = useMemo(() => {
    if (!isObject(themeState?.colors?.[name])) return false;
    return !!Object.keys(themeState.colors![name]).some((key) =>
      Boolean(themeState.colors![name][key])
    );
  }, [themeState, name]);

  const [contrastRatio, contrastIsGood] = useMemo(() => {
    const c = combinedThemeColors?.primary?.['300'];
    if (!c) return [null, false];
    const ratio = round(hex(c as string, '#fff'), 2);
    return [ratio, ratio >= 3];
  }, [combinedThemeColors?.primary]);

  return (
    <Box w="100%">
      <Flex w="100%" align="center" justify="space-between" mb={4}>
        <Flex align="center">
          <Text textStyle="subtitle2" mr={2}>
            {name}
          </Text>
          <Tooltip label={tips[name]} aria-label="A tooltip">
            <Icon as={Info} />
          </Tooltip>
        </Flex>
        <ButtonGroup size="sm">
          {isOpen && localStateHasData && (
            <Button colorScheme="danger" variant="tertiary" onClick={handleRestore}>
              Restore
            </Button>
          )}
          {isOpen && localStateHasData && (
            <Button colorScheme={name} variant="solid" onClick={handleSave} isLoading={isLoading}>
              Save {name}
            </Button>
          )}
          <Button onClick={onToggle} variant="tertiary" colorScheme="base">
            {isOpen ? 'Cancel' : 'Edit'}
          </Button>
        </ButtonGroup>
      </Flex>
      <AnimateSharedLayout>
        <Stack direction={isOpen ? 'column' : 'row'}>
          {Object.keys(colors).map((key) => {
            const id = `${name}-${key}`;
            return (
              <AnimatePresence key={id}>
                <HStack spacing={4} align="center">
                  <AnimatedColorBox
                    color={colors[key]}
                    isMain={Number(key) === 300}
                    layoutId={id}
                  />
                  {isOpen && (
                    <MotionBox
                      display="flex"
                      flex={1}
                      animate={{ opacity: [0, 1], x: [100, 0], transition: { duration: 0.3 } }}
                    >
                      <Text textStyle="subtitle1">{colors[key]}</Text>
                    </MotionBox>
                  )}
                  {isOpen && Number(key) === 300 && (
                    <MotionBox
                      display="flex"
                      animate={{ opacity: [0, 1], x: [100, 0], transition: { duration: 0.45 } }}
                    >
                      <ColorPicker
                        currentColor={colors[key]}
                        onChange={(value) => handleChange(key, value)}
                      />
                    </MotionBox>
                  )}
                </HStack>
              </AnimatePresence>
            );
          })}
        </Stack>
      </AnimateSharedLayout>
      {!!contrastRatio && !contrastIsGood && (
        <Banner
          status="warning"
          mt={8}
          description={
            <Text>
              Your {item.variant} color has a contrast ratio of{' '}
              <chakra.span fontWeight="semibold">{contrastRatio}:1</chakra.span> against a white
              background and may make it difficult for users with vision impairment to access your
              site. <br /> <br />
              The recommended contrast ratio is at least{' '}
              <chakra.span fontWeight="semibold">3:1</chakra.span>.{' '}
              <Link
                isExternal
                href="https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html"
                textDecor="underline"
                aria-label="Read more about web accessibility standards here (opens in new window)"
              >
                Read more about web accessibility standards here.
              </Link>
            </Text>
          }
        />
      )}
    </Box>
  );
};
